import { Component, OnInit, Input } from '@angular/core'

import * as _ from 'lodash'

import { CredentialListItem } from '../models/credential-list-item'
import { EligibilityRule } from '../../models/eligibility-rule-set'
import { EligibilityStatusTag, Organization } from '../../models'
import { MembershipCredentialItem } from '../models/membership-credential-item'
import { MembershipItem } from '../models/membership-item'

@Component({
  standalone: false,
  selector: 'app-membership-item',
  templateUrl: './membership-item.component.html',
  styleUrls: ['./membership-item.component.scss']
})
export class MembershipItemComponent implements OnInit {

  @Input() public membershipItem: MembershipItem

  public credentialListItems: CredentialListItem[] = []
  public eligibilityStatusTag: EligibilityStatusTag
  public expanded: boolean
  public membershipCredentialItem: MembershipCredentialItem
  public showDropdown: boolean
  public subtitle: string

  private memdefMap: any
  private orgMap: any

  public ngOnInit(): void {
    const { membership, organization } = this.membershipItem
    this.memdefMap = this.buildMemdefMap
    this.orgMap = this.buildOrgMap
    this.subtitle = membership.isAssigned ? `Assigned to ${this.affiliatedOrgName}` : organization.name
    this.showDropdown = membership.hasEligibility || membership.membership_definition.hasParentMembershipDefinitions
    this.eligibilityStatusTag = membership.tag(organization.timezoneOrDefault)
    if (this.showDropdown) {
      this.createCredentialListItems()
    } else {
      this.membershipCredentialItem = this.createMembershipCredentialItem()
    }
  }

  public toggleExpanded(): void {
    this.expanded = !this.expanded
  }

  private get buildMemdefMap(): any {
    const { membership_definition, parent_membership_definitions } = this.membershipItem.membership
    return [membership_definition, ...parent_membership_definitions].reduce((acc, cur) => {
      acc[cur.id] = cur
      return acc
    }, { })
  }

  private get buildOrgMap(): any {
    const { membership, organization } = this.membershipItem
    const parentOrganizations = membership.parent_membership_definitions.map((pmd) => new Organization(pmd.organization))
    return [organization, ...parentOrganizations].reduce((acc, cur) => {
      acc[cur.id] = cur
      return acc
    }, { })
  }

  private get affiliatedOrgName(): string {
    const { affiliation_organization_id, membership_affiliation_path } = this.membershipItem.membership
    return membership_affiliation_path.find((p) => p.organization_id === affiliation_organization_id).organization_name
  }

  private createCredentialListItems(): void {
    this.membershipItem.membership.eligibility_rule_set.eligibility_rules
      .filter((rule) => rule.originator_type === 'membership_definition')
      .forEach((rule) => this.credentialListItems.push(this.createCredentialListItem(rule)))

    this.credentialListItems.sort(this.parentFirst.bind(this))
  }

  /**
   * Places parent memberships at the top of the list
   */
  private parentFirst(credentialListItem: CredentialListItem): number {
    if (credentialListItem.organization.id === this.membershipItem.organization.id) return 1
    return -1
  }

  private createCredentialListItem(rule: EligibilityRule): CredentialListItem {
    const ruleSetId = rule.linked_eligibility_rule_set_id || rule.eligibility_rule_set_id
    const memdef = this.memdefMap[rule.originator_id]
    const organization = this.orgMap[memdef.boss_organization_id]
    const credentialListItem = new CredentialListItem({
      organization,
      rules: this.membershipItem.membership.eligibility_rule_set.eligibility_rules.filter((er) => this.filterRule(er, ruleSetId)),
      credentials: this.getCredentials(ruleSetId, organization)
    })
    return credentialListItem
  }

  private createMembershipCredentialItem(): MembershipCredentialItem {
    const { membership, organization } = this.membershipItem
    const credential = membership.persona_eligibility.eligibility_credentials.find((ec) => ec.credential_value === membership.id)
    const rule = membership.eligibility_rule_set.eligibility_rules[0]
    return new MembershipCredentialItem({ credential, rule, organization })
  }

  private filterRule(rule: EligibilityRule, ruleSetId: string): boolean {
    const { eligibility_rule_set_id, linked_eligibility_rule_set_id } = rule
    return linked_eligibility_rule_set_id ? linked_eligibility_rule_set_id === ruleSetId : eligibility_rule_set_id === ruleSetId
  }

  private getCredentials(ruleSetId: string, organization: Organization): any {
    const { membership } = this.membershipItem
    const creds = membership.persona_eligibility.eligibility_credentials.filter((ec) => {
      const { credential_type, credential_value, eligibility_status, linked_eligibility_rule_set_id, rule_value } = ec
      if (linked_eligibility_rule_set_id) return linked_eligibility_rule_set_id === ruleSetId
      if (credential_type === 'membership') return credential_value === membership.id
      if (credential_type === 'suspension') return eligibility_status === 'suspended' && rule_value === organization.id
      return true
    })
    /**
     * credGroup
     *
     * {
     *  "111-111-111-111": [
     *    mem_def_credential1,
     *    mem_def_credential2
     *  ],
     *  "222-222-222-222": [
     *    waiver_credential
     *  ]
     * }
     */
    const credGroup = creds.reduce((acc, cur) => {
      acc[cur.rule_value] = [...(acc[cur.rule_value] || []), cur]
      return acc
    }, { })
    return Object.values(credGroup).map((credentials) => this.bestCredential(credentials))
  }

  /**
   * In the case above, where there are two mem_def_credentials, this will try and find the best one that
   * satisfies the rule set. First it will compare the credentials to the membership: If the membership is
   * valid and the credential is valid and they both have the same effective_at date, pick that one etc. Second,
   * it will try and find the most valid credential without accounting for the membership. Third, it will
   * just return the first credential.
   */
  private bestCredential(credentials): any {
    if (credentials.length > 1) {
      const mostRel = credentials.find((cred) => {
        return cred.is_most_relevant
      })

      if (mostRel) {
        return mostRel
      }

      return this.find(credentials, this.membershipItem.membership) || this.find(credentials) || credentials[0]
    }
    return credentials[0]
  }

  private find(credentials, membership?): any {
    const valid: any = { eligibility_status: 'valid' }
    const eligComplete: any = { eligibility_status: 'complete' }
    const expiringSoon: any = { eligibility_status: 'expiring soon' }
    const upcoming: any = { eligibility_status: 'upcoming' }
    const complete: any = { status: 'complete' }

    if (membership) {
      const effective_at = membership.effective_at
      Array.from([valid, eligComplete, expiringSoon, upcoming, complete]).forEach((match) => match.effective_at = effective_at)
    }
    return _.find(credentials, valid) ||
      _.find(credentials, eligComplete) ||
      _.find(credentials, expiringSoon) ||
      _.find(credentials, upcoming) ||
      _.find(credentials, complete)
  }

}
